import { ConfirmationDialog } from "@components/ConfirmationDialog";
import { FormInput } from "@components/FormInput";
import { FormMultiSelect } from "@components/FormMultiSelect";
import { FormSelect } from "@components/FormSelect";
import { Container } from "@components/crud/Container";
import { Footer } from "@components/crud/Footer";
import { Form } from "@components/crud/Form";
import { Toolbar } from "@components/crud/Toolbar";
import { useApiSelectOptions } from "@hooks/useApiSelectOptions";
import Grid from "@mui/material/Unstable_Grid2";
import {
  ModelMedia,
  useAdminGameConceptPost,
  useAdminPositionGet,
  useAdminSkillGet,
  useAdminSportGet
} from "@sportsgravyengineering/sg-api-react-sdk";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Typography, FormLabel, capitalize } from "@mui/material";
import { styled } from "@mui/material/styles";
import { MediaSelector } from "@components/MediaSelector";
import GamePickerImage from "@assets/images/gameConceptPicker.png";
import { useRecoilState } from "recoil";
import { organizationAtom } from "../../recoil/auth";
import { uploadMediaUsingPresignedUrl } from "@services/customNetworkCalls";
import { capitalizeEveryWord } from "@utils/capitalize";

const StyledFormLabel = styled(FormLabel)(({ theme }) => ({
  marginBottom: "0.25rem",

  "& .MuiFormLabel-asterisk": {
    color: theme.palette.error.main
  }
}));
export const GameConceptCreate = () => {
  const [filestoUpload, setFilesToUpload] = useState<File | ModelMedia | null>(
    null
  );
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [mediaUploadLoading, setMediaUploadLoading] = useState(false);
  const [resetMedia, setResetMedia] = useState(false);
  const [positions, setPositions] = useState<
    {
      positionId: string;
    }[]
  >([]);
  const [skills, setSkills] = useState<
    {
      skillId: string;
    }[]
  >([]);
  const [organizationId] = useRecoilState(organizationAtom);
  const {
    handleSubmit,
    control,
    formState: { isValid },
    setValue,
    reset
  } = useForm({
    mode: "onBlur"
  });
  const { data: sports, isLoading: isSportLoading } = useAdminSportGet({
    organizationId: organizationId!
  });
  const sportOptions = useMemo(
    () =>
      sports?.data?.map((sport) => ({
        label: sport.name!,
        value: sport.sportId!
      })) || [],
    [sports]
  );
  useEffect(() => {
    if (sportOptions.length == 1) {
      setValue("sportId", sportOptions[0].value);
      setValue("positions", []);
      setPositions(sportOptions[0].value);
      setValue("skills", []);
      setSkills(sportOptions[0].value);
    }
  }, [sportOptions]);
  const { options: positionOptions, isLoading: positionOptionsLoading } =
    useApiSelectOptions({
      api: useAdminPositionGet,
      dataField: "positions",
      labelField: "name",
      valueField: "positionId",
      params: {
        sportId: positions
      },
      options: {
        query: {
          enabled: !!positions
        }
      }
    });

  const { options: skillOptions, isLoading: skillOptionsLoading } =
    useApiSelectOptions({
      api: useAdminSkillGet,
      dataField: "skills",
      labelField: "name",
      valueField: "skillId",
      params: {
        sportId: skills
      },
      options: {
        query: {
          enabled: !!skills
        }
      }
    });

  const { mutate: save, isLoading: isSaving } = useAdminGameConceptPost();

  const saveHandler =
    (resetInsteadOfRoute = false) =>
    async (formValues) => {
      setMediaUploadLoading(true);
      const filesPromises = await Promise.all(
        filestoUpload.map((file) => {
          if (file instanceof File) {
            const promise = uploadMediaUsingPresignedUrl(file);
            console.log("PROMISE", promise);
            return promise;
          }
        })
      );
      const values = {
        ...formValues,
        ...(organizationId && { organizationId: organizationId })
      };
      if (values.positions) {
        values.positions = values.positions.map((position) => ({
          positionId: position
        }));
      }
      if (values.skills) {
        values.skills = values.skills.map((skill) => ({
          skillId: skill
        }));
      }
      values["mediaIds"] = filesPromises;
      setMediaUploadLoading(false);
      values["name"] = capitalize(values.name);
      save(
        {
          data: values
        },
        {
          onSuccess: () => {
            enqueueSnackbar("Game concept added successfully!", {
              variant: "success"
            });
            if (resetInsteadOfRoute) {
              reset();
              setResetMedia(!resetMedia);
              setFilesToUpload(null);
            } else {
              navigate("/game-concepts");
            }
          },
          onError: () => {
            enqueueSnackbar("Failed to add game concept!", {
              variant: "error"
            });
          }
        }
      );
    };

  return (
    <Container>
      <Toolbar title="Add Game Concept" />
      <Form>
        <Grid data-testid="gameconcept-add-form" container spacing={3}>
          <Grid data-testid="gameconcept-name" xs={12} md={6}>
            <FormInput
              control={control}
              name="name"
              type="text"
              label="Name"
              required={true}
              rules={{
                required: "Name is required"
              }}
              onChange={(e) => {
                setValue("name", capitalizeEveryWord(e.target.value));
              }}
            />
          </Grid>
          <Grid data-testid="gameConcept-sport" xs={12} md={6}>
            <FormSelect
              control={control}
              name="sportId"
              label="Sport"
              required={true}
              isLoading={isSportLoading}
              disabled={sportOptions.length === 1}
              options={sportOptions}
              onChange={(e) => {
                setValue("positions", []);
                setPositions(e.target.value);
                setValue("skills", []);
                setSkills(e.target.value);
              }}
              rules={{
                required: "Sport is required"
              }}
            />
          </Grid>
          <Grid data-testid="gameConcept-skill" xs={12} md={6}>
            <FormMultiSelect
              control={control}
              name="skills"
              label="Skills"
              value="skills"
              required={false}
              isLoading={skillOptionsLoading}
              options={skillOptions}
            />
          </Grid>
          <Grid data-testid="gameConcept-position" xs={12} md={6}>
            <FormMultiSelect
              control={control}
              name="positions"
              label="Positions"
              value="positions"
              required={false}
              isLoading={positionOptionsLoading}
              options={positionOptions}
            />
          </Grid>
          <Grid data-testid="gameConcept-description" xs={12} md={12}>
            <FormInput
              control={control}
              name="description"
              type="text"
              label="Description"
              required={false}
              multiline={true}
            />
          </Grid>
          <Grid xs={12} md={12}>
            <StyledFormLabel>
              <Typography display="inline" variant="formLabel">
                {"Photo/Video Content"}
              </Typography>
            </StyledFormLabel>
          </Grid>
          <MediaSelector
            uploadedFiles={[]}
            setFilesToUpload={(files) => {
              //@ts-ignore
              setFilesToUpload(files);
            }}
            imagePlaceHolder={GamePickerImage}
            resetMedia={resetMedia}
          />
        </Grid>
      </Form>
      <Footer
        cancelBtnClick={() => setOpenCancelDialog(true)}
        saveBtnClick={handleSubmit(saveHandler(false))}
        saveAndNewBtnClick={handleSubmit(saveHandler(true))}
        isDisabled={!isValid || isSaving || mediaUploadLoading}
        isLoading={isSaving || mediaUploadLoading}
      />
      <ConfirmationDialog
        title="Are you sure you want to cancel?"
        body="All of your current changes will be lost."
        open={openCancelDialog}
        close={() => setOpenCancelDialog(false)}
        onCancel={() => setOpenCancelDialog(false)}
        onConfirm={() => navigate("/game-concepts")}
        cancelBtnText="Cancel"
        confirmBtnText="Confirm"
      />
    </Container>
  );
};
